import React from 'react';
import { Component } from '@/types';
import { VotingCard } from '@/components/pm/VotingCard';
import { useQueryTemplateData, isQueryTemplateDataSource } from '@/hooks/useQueryTemplateData';
import { ChartDataSource } from '@/types/chartDataSource';

export interface SharedVotingCardComponentProps {
  component: Component;
  isViewerMode?: boolean;
}

export const SharedVotingCardComponent: React.FC<SharedVotingCardComponentProps> = ({
  component,
  isViewerMode = false,
}) => {
  const { properties = {} } = component;

  const dataSource = properties.dataSource as ChartDataSource | undefined;
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);

  const {
    data: queryTemplateData,
    isLoading,
    error,
    refetch,
  } = useQueryTemplateData({
    dataSource,
    componentId: component.id,
    enabled: hasQueryTemplate,
    initialData: properties.sampleData as Record<string, unknown>[] | undefined,
  });

  const resolvedData: Record<string, unknown>[] | undefined =
    queryTemplateData
    ?? (Array.isArray(properties.sampleData) ? (properties.sampleData as Record<string, unknown>[]) : undefined);

  if (isLoading && !queryTemplateData) {
    return (
      <div
        data-testid="voting-card-loading"
        className="w-full h-full flex items-center justify-center gap-2 text-gray-400 text-sm"
      >
        <svg className="animate-spin" style={{ width: 20, height: 20 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12a9 9 0 11-6.219-8.56" />
        </svg>
        Loading voting data...
      </div>
    );
  }

  if (error && !queryTemplateData) {
    return (
      <div data-testid="voting-card-error" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, color: '#ef4444' }}>{error}</span>
        <button onClick={() => refetch()} style={{ fontSize: 12, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <VotingCard
      component={component}
      data={resolvedData}
      isViewerMode={isViewerMode}
    />
  );
};

SharedVotingCardComponent.displayName = 'SharedVotingCardComponent';
